import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const isExternalLink = (href) => href.startsWith('http');

  const footerLinks = [
    {
      title: 'Navigation',
      links: [
        { name: 'Accueil', href: '#hero' },
        { name: 'À Propos', href: '#about' },
        { name: 'Médecins', href: '#doctors' },
        { name: 'Soins', href: '#services' },
      ],
    },
    {
      title: 'Services',
      links: [
        { name: 'Hydrafacial', href: '#services' },
        { name: 'Fotona Laser', href: '#services' },
        { name: 'Épilation Laser', href: '#pricing' },
        { name: 'Tarifs', href: '#pricing' },
      ],
    },
    {
      title: 'Zones desservies',
      links: [
        { name: 'Près de Lens', to: '/medecine-esthetique-lens' },
        { name: 'Près de Liévin', to: '/medecine-esthetique-lievin' },
        { name: 'Près de Hénin-Beaumont', to: '/medecine-esthetique-henin-beaumont' },
        { name: 'Hauts-de-France', to: '/medecine-esthetique-pas-de-calais' },
        { name: 'Hydrafacial près de Lens', to: '/hydrafacial-lens-rouvroy' },
      ],
    },
    {
      title: 'Contact',
      links: [
        { name: '06 85 95 10 05', href: 'tel:+33685951005' },
        { name: 'medesthetique@outlook.com', href: 'mailto:medesthetique@outlook.com' },
        { name: 'WhatsApp', href: 'https://wa.me/33685951005' },
      ],
    },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/mn_medecine_esthetique/?hl=am-et', label: 'Instagram' },
  ];

  const handleClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-gradient-to-b from-gray-50 to-gray-100 pt-20 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-light mb-4">
                <span className="text-[#1e3a5f] font-medium">MN</span>
                <span className="text-gray-700 ml-2">Médecine Esthétique</span>
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Une histoire de famille, de science et d'équilibre. Où la médecine devient art, et
                l'art devient soin.
              </p>
              <address className="not-italic text-sm text-gray-600 mb-6 leading-relaxed">
                Maison Medicale de Rouvroy
                <br />
                90 rue Louis Daubenton
                <br />
                62320 Rouvroy
              </address>
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    target={isExternalLink(social.href) ? '_blank' : undefined}
                    rel={isExternalLink(social.href) ? 'noopener noreferrer' : undefined}
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-600 hover:bg-[#1e3a5f] hover:text-white transition-all duration-300 shadow-md"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon size={18} />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Links */}
          {footerLinks.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <h4 className="text-gray-900 font-medium mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.name}>
                    {link.to ? (
                      <Link
                        to={link.to}
                        className="text-gray-600 hover:text-[#1e3a5f] transition-colors duration-300 text-sm"
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        onClick={(e) => handleClick(e, link.href)}
                        className="text-gray-600 hover:text-[#1e3a5f] transition-colors duration-300 text-sm"
                      >
                        {link.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-300 mb-8" />

        {/* Wedreamteam Partner */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-8 text-center"
        >
          <p className="text-xs text-gray-500 mb-2">Site réalisé par</p>
          <a
            href="https://wedreamteam.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-[#1e3a5f] hover:text-[#2a5585] font-medium transition-colors duration-300"
          >
            Wedreamteam
          </a>
        </motion.div>

        {/* Bottom Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
        >
          <p className="text-sm text-gray-600">
            © {currentYear} MN Médecine Esthétique. Tous droits réservés.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/mentions-legales" className="text-gray-600 hover:text-[#1e3a5f] transition-colors">
              Mentions légales
            </Link>
            <Link to="/politique-confidentialite" className="text-gray-600 hover:text-[#1e3a5f] transition-colors">
              Politique de confidentialité
            </Link>
            <Link to="/cgv" className="text-gray-600 hover:text-[#1e3a5f] transition-colors">
              CGV
            </Link>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;